import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { getMonthName } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import ReportData from "go-report/reports";
import { getReportCustomFieldsResources, reportParamsConverter } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";

interface Props {
	resource: Record<string, any>;
}

const listName = "ACCESS_REPORT";
const resourceType = "ACCESS_REPORT";

const Report = ({ resource }: Props) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	let config = {
		reportConfigId: "admin_access_report",
		aggregatePrefix: "summary",
		selectedColumns: ["access", "used", "balance"],
		groups: [
			{
				id: "ACCESS",
				name: t("modules.access.field.access.title"),
			},
			{
				id: "ORGANIZATION_NAME",
				name: t("lib:common.word.location"),
			},
			{
				id: "PARTNER_CS",
				name: "Partner CS",
			},
			{
				id: "PARTNER_SALES",
				name: "Partner Sales",
			},
			{
				id: "PARTNER",
				name: "Partner",
			},
			{
				id: "FIRST_PAID_AT",
				name: t("common.word.first_paid_at"),
			},
			{
				id: "FIRST_PAID_AT_DAY",
				name: t("modules.access.field.first_paid_at_day.title"),
			},
			{
				id: "FIRST_PAID_AT_MONTH",
				name: t("modules.access.field.first_paid_at_month.title"),
			},
			{
				id: "FIRST_PAID_AT_YEAR",
				name: t("modules.access.field.first_paid_at_year.title"),
			},
			{
				id: "LICENSE_TYPE",
				name: t("common.word.license_type"),
			},
			{
				id: "COMPANY_ID",
				name: t("common.word.company_id"),
			},
		],
		showPercent: true,
		columns: [
			{
				id: "access",
				name: t("modules.access.field.access.title"),
			},
			{
				id: "used",
				name: t("modules.access.field.used.title"),
			},
			{
				id: "balance",
				name: t("modules.access.field.balance.title"),
			},
		],
		selectedGroups: ["NONE"],
		groupsInfo: {
			LICENSE_TYPE: (groupByValue: Record<string, any>) => {
				return t(`enums.licenses.${groupByValue.name}`, { ns: "admin" });
			},
			FIRST_PAID_AT_MONTH: (groupByValue: Record<string, any>) => getMonthName(groupByValue.name, t),
		},
		filters: [
			{
				id: "balance",
				type: "number",
				name: t("modules.access.field.balance.title"),
			},
			{
				id: "access",
				type: "search_select",
				name: t("modules.access.field.access.title"),
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getAccessTypes(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "organization",
				name: t("lib:common.word.location"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminOrganizationsListSearchSelect(search, params, undefined, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "partner",
				name: "Partner",
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getAdminPartnersListSearchSelect(search, params, options),
				},
			},
			{
				id: "first_paid_at",
				name: t("common.word.first_paid_at"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "license_type",
				name: t("common.word.license_type"),
				type: "list",
				options: {
					STANDARD: t("enums.licenses.STANDARD"),
					FUTURE: t("enums.licenses.FUTURE"),
					CANCELLED: t("enums.licenses.CANCELLED"),
					TRIAL: t("enums.licenses.TRIAL"),
					PAUSED: t("enums.licenses.PAUSED"),
					MANUAL: t("enums.licenses.MANUAL"),
					NONE: t("enums.licenses.NONE"),
				},
			},
			{
				id: "company_id",
				name: t("common.word.company_id"),
				type: "text",
				typeOptions: ["c", "e"],
			},
		],
		segments: [
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: ["access", "used", "balance"],
				filters: [],
				groups: [],
			},
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				adminApi.getAdminAccessReport(reportParamsConverter(params), { cancelToken: sourceToken?.token }),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.access.report.header.title"),
			filename: t("modules.access.report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["ORGANIZATION"], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const AdminAccessReportPage = () => {
	const [resource, setResource] = useState<any>();
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "ORGANIZATION",
			name: t(`enums.custom_fields.resources.ORGANIZATION`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.access.report.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.access.report.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} />
			</Suspense>
		</div>
	);
};

export default AdminAccessReportPage;
